import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Rotate-a-BYOK-key form (#971), modeled on `AddKeyModal`: one masked input
// for the NEW raw secret, handed up on submit; the parent owns the mutation +
// busy/error state (`createKeyRotate`). A 422 renders `error` inline and the
// dialog stays open — the copy already told the user the old key stays live,
// so closing on a refusal would read as "rotated". The secret only lives in
// this local until POSTed and sealed server-side; it is never logged.

export type RotateKeyModalProps = {
  readonly open: boolean;
  /** Display name of the provider whose key is being rotated. */
  readonly provider: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (input: { readonly key: string }) => void;
  readonly onClose: () => void;
};

export function RotateKeyModal(props: RotateKeyModalProps): JSX.Element {
  const [key, setKey] = createSignal('');

  // The component stays mounted across close/reopen, so a raw secret typed for
  // one rotation would survive into the next — prefilling (and possibly
  // submitting) the prior credential for a different key. Clear on every close.
  const close = (): void => {
    setKey('');
    props.onClose();
  };

  const submit = (e: Event): void => {
    e.preventDefault();
    if (key().trim() === '') return;
    props.onSubmit({ key: key().trim() });
  };

  return (
    <Modal
      open={props.open}
      title={t('account_keys.rotate_title')}
      desc={t('account_keys.rotate_body', { provider: props.provider })}
      onClose={close}
    >
      <form class="rotate-key__form" onSubmit={submit}>
        <Field label={t('account_keys.key')} id="rotate-key-secret">
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="password"
              value={key()}
              onInput={(e) => setKey(e.currentTarget.value)}
              placeholder={t('account_keys.rotate_placeholder')}
              autocomplete="off"
              required
            />
          )}
        </Field>
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
        <div class="dialog__footer">
          <Button variant="outline" onClick={close}>
            {t('account_keys.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={props.busy || key().trim() === ''}>
            {props.busy ? t('account_keys.rotating') : t('account_keys.rotate_submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
